import { UnleashClient } from 'unleash-proxy-client'
import Config from '@/config'
import sentry from './sentry'
import { flags } from '@/utils/abtest.utils'
import { getUnleashOverride } from '@/utils/unleashOverride.util'
import { saveCachedUnleashContext, getCachedUnleashContext } from '@/vuex/modules/loading-page/loading-page.utils'
import { readRawSessionCookie } from './unleashSession.constants'

// Seeds the initial context; main.ts authoritatively re-sets it via setSessionId() right after mint.
export const readSessionCookie = readRawSessionCookie

// AHA-48351: the cached profile context restored at boot, minus the fields the SDK
// owns (appName/environment are static; sessionId is re-set from the cookie by
// setSessionId). featureToggles/updateFeatureContext merges over it so a partial,
// pre-login update can't wipe it — see the comment there.
const toRestoredContext = (context) => {
  if (!context) return null
  const {
    appName, environment, sessionId, ...rest
  } = context
  return rest
}

// Order-independent form of an Unleash context, as the SDK keys its stored toggles
// (unleash-proxy-client contextString). Unset fields are dropped, as the SDK's query drops them.
const contextKey = ({ properties = {}, ...fields }) => {
  const entries = (record) => Object.entries(record)
    .filter(([, value]) => value !== undefined && value !== null)
    .sort(([a], [b]) => a.localeCompare(b))
  return JSON.stringify([entries(fields), entries(properties)])
}

class UnleashService {
  constructor(additionalParams = {}) {
    try {
      this.unleashClient = new UnleashClient({
        url: Config.unleashEndpoint,
        clientKey: Config.unleashClientKey,
        appName: 'presenter',
        createAbortController: () => null,
        // This is the on-update function for real-time updates from Unleash to continuously refresh feature flags from the Unleash server.
        // Developers should carefully consider which features to use with this function, as it needs to handle cases where a feature might be toggled on or off while users are actively using it.
        // Additionally, there may be unforeseen cases to account for.
        refreshInterval: 0,
        context: {
          sessionId:
            readSessionCookie()
            || localStorage.getItem('unleash:repository:sessionId')?.replace(/^"|"$/g, '')
            || undefined,
        },
        ...additionalParams,
      })
      this.restoredContext = toRestoredContext(additionalParams.context)
      this.ready = new Promise((resolve) => this.unleashClient.on('ready', resolve))
    } catch (error) {
      sentry.captureException(error)
      this.unleashClient = null
      // in case of error, don’t leave `ready` hanging
      this.ready = Promise.resolve()
    }
  }

  // Ideally, Only 2 requests to Unleash proxy should be made
  // 1 for the anonymous (with sessionId is the stickiness)
  // 2 for the user context (with userId is the stickiness)
  start() {
    if (flags.canUseNewUnleashMechanism) {
      this.startV2()
    } else {
      this.startV1()
    }
  }

  startV1() {
    this.unleashClient.start()
  }

  startV2() {
    // only start once!
    // can create a v2 function to easier to flag
    if (this.unleashClient && !this.isStarted) {
      this.isStarted = true
      if (flags.canUseCacheProfileOnLoad) {
        this.unleashClient.on('update', () => {
          // whenever there is update, capture the current context
          // and store it to bootstrap
          const context = this.unleashClient.getContext()
          // AHA-48351: never re-persist an identity that only came from the restored cache —
          // its session may since have been found dead and the cache cleared. Only an
          // identity confirmed by an applied auth context is saved.
          if (context.isAuth && !this.hasAppliedAuthContext) return
          saveCachedUnleashContext(context)
        })
      }
      this.unleashClient.start()
    }
  }

  // AHA-48351: the restored profile context only stands in for the user until their session
  // is confirmed. It is gone once dropped (logged out at boot, or an auth context applied),
  // or once its cache entry was cleared meanwhile (logout, or a dead session:
  // fetchAndSaveCurrentUserAction → clearAllCachedData).
  getRestoredContext() {
    if (this.restoredContext && !getCachedUnleashContext()) {
      this.restoredContext = null
    }
    return this.restoredContext
  }

  dropRestoredContext() {
    this.restoredContext = null
  }

  markAuthContextApplied() {
    this.restoredContext = null
    this.hasAppliedAuthContext = true
  }

  // AHA-48350: whether the toggles this client holds were fetched for exactly `context` (as
  // updateContext would apply it) within `maxAgeMs`. togglesStorageTTL only spares the SDK's
  // initial fetch: on init it restores the toggles another page of this origin stored, with the
  // time they were fetched for that context, and updateContext then refetches regardless. Must be
  // asked before updateContext, which replaces the context compared against.
  holdsFreshTogglesFor(context, maxAgeMs) {
    try {
      const client = this.unleashClient
      // The SDK's record of when its toggles were fetched for its current context (0 when none).
      const fetchedAt = client?.lastRefreshTimestamp
      if (typeof fetchedAt !== 'number' || fetchedAt <= 0 || Date.now() - fetchedAt > maxAgeMs) {
        return false
      }
      const current = client.getContext()
      const { environment, appName, sessionId } = current
      return contextKey(current) === contextKey({
        environment, appName, sessionId, ...context,
      })
    } catch (error) {
      return false
    }
  }

  isFeatureEnabled(featureName) {
    // BOO-203: honor the URL-param override (on the allowlisted envs — local,
    // develop and production; see unleashOverride.util.js) so the few call sites
    // that read the raw client instead of the Vuex getters are covered too.
    const override = getUnleashOverride(featureName)
    if (override) return override.enabled

    if (!this.unleashClient) {
      return false
    }

    try {
      return this.unleashClient.isEnabled(featureName)
    } catch (error) {
      return false
    }
  }

  getVariant(featureName) {
    // BOO-203: honor the URL-param override (allowlisted envs) — see isFeatureEnabled.
    const override = getUnleashOverride(featureName)
    if (override) return override.variant

    if (!this.unleashClient) {
      return { name: 'disabled', enabled: false }
    }

    try {
      return this.unleashClient.getVariant(featureName)
    } catch (error) {
      return { name: 'disabled', enabled: false }
    }
  }

  // Push the shared aha_unleash_session id into the client context so presenter
  // client-side flags bucket on the SAME anchor as the pricing page + the
  // server-side /entitlements path. Called at bootstrap (before start()), and again
  // when general-api resets the cookie mid-session (BOO-1279) — after start() the
  // SDK re-fetches the toggles for the new id.
  setSessionId(sessionId) {
    if (!this.unleashClient || !sessionId) return
    try {
      this.unleashClient.setContextField('sessionId', sessionId)
    } catch (error) {
      sentry.captureException(error)
    }
  }
}
let params = {}
if (flags.canUseCacheProfileOnLoad) {
  const cachedUnleashContext = getCachedUnleashContext()
  params = cachedUnleashContext ? {
    experimental: {
      // make it 1 week — in SECONDS: the SDK multiplies togglesStorageTTL by 1000
      // this only affects the initialFetch
      // we always trigger fetch again when updateContext
      // https://github.com/Unleash/unleash-js-sdk/blob/85e83a7f060d601bca1f6825e0f9501394159000/src/index.ts#L544
      togglesStorageTTL: 60 * 60 * 24 * 7,
    },
    context: cachedUnleashContext,
  } : {}
}
export default new UnleashService(params)
